/**
 * SquareCardForm — secure card-on-file capture via Square's Web Payments SDK.
 *
 * Card data is entered inside Square's hosted iframe fields; the raw card
 * number NEVER touches GLPFlo's DOM or JS. On submit we tokenize (intent
 * "STORE") and hand the parent an opaque single-use nonce that the backend
 * exchanges for a card-on-file.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";

const SQUARE_ENV = process.env.REACT_APP_SQUARE_ENV || "sandbox";
const APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;
const SDK_URL =
  SQUARE_ENV === "production"
    ? "https://web.squarecdn.com/v1/square.js"
    : "https://sandbox.web.squarecdn.com/v1/square.js";

function loadSquareSdk() {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    let s = document.getElementById("square-web-sdk");
    if (s) {
      s.addEventListener("load", () => resolve(window.Square));
      s.addEventListener("error", () => reject(new Error("sdk-load-failed")));
      return;
    }
    s = document.createElement("script");
    s.id = "square-web-sdk";
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => resolve(window.Square);
    s.onerror = () => reject(new Error("sdk-load-failed"));
    document.head.appendChild(s);
  });
}

const SquareCardForm = forwardRef(function SquareCardForm(_props, ref) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const cardRef = useRef(null);
  const containerId = useRef(`sq-card-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    let mounted = true;
    let cardInstance = null;
    (async () => {
      try {
        if (!APP_ID || !LOCATION_ID) throw new Error("missing-config");
        const Square = await loadSquareSdk();
        if (!mounted) return;
        const payments = Square.payments(APP_ID, LOCATION_ID);
        const card = await payments.card();
        await card.attach(`#${containerId}`);
        if (!mounted) {
          try { card.destroy(); } catch (_e) { /* noop */ }
          return;
        }
        cardInstance = card;
        cardRef.current = card;
        setReady(true);
      } catch (_e) {
        if (mounted) setError("Could not load the secure card form. Please refresh and try again.");
      }
    })();
    return () => {
      mounted = false;
      try { cardInstance?.destroy(); } catch (_e) { /* noop */ }
    };
  }, [containerId]);

  useImperativeHandle(
    ref,
    () => ({
      isReady: () => ready,
      tokenize: async (billingContact) => {
        if (!cardRef.current) throw new Error("Card form is not ready yet.");
        // STORE intent saves the card on file — Square rejects `amount`/
        // `currencyCode` for STORE, so we only send verification context.
        const result = await cardRef.current.tokenize({
          intent: "STORE",
          customerInitiated: true,
          sellerKeyedIn: false,
          billingContact: billingContact || {},
        });
        if (result.status !== "OK") {
          const msg = result?.errors?.[0]?.message || "The card could not be verified. Please check the details.";
          throw new Error(msg);
        }
        return result.token;
      },
    }),
    [ready]
  );

  return (
    <div data-testid="square-card-form">
      <div
        id={containerId}
        className="rounded-xl border border-slate-200 bg-white p-3 min-h-[56px]"
      />
      {!ready && !error && (
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-2" data-testid="square-card-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading secure card form…
        </div>
      )}
      {error && (
        <p className="text-xs text-rose-600 mt-2" data-testid="square-card-error">{error}</p>
      )}
      <p className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-2">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
        Card details are entered securely on Square. GLPFlo never sees or stores your card number.
      </p>
    </div>
  );
});

export default SquareCardForm;
